import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import ArrowRight from 'assets/svgs/arrowRight24.svg';
import * as dashboardActions from 'features/dashboard/duck';
import * as trainingActions from 'features/training/duck';
import * as loginActions from 'core/duck';
import * as notificationActions from 'features/notifications/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import {
  DashboardProfile,
  UserAssignments,
  DashboardInvitation,
  TermsAndConditions,
} from 'features/dashboard/';
import { Accordion, Card, TrainingModules, AlertNotice, AlertMessage } from 'shared/components';
import { getMappedAssignments } from 'features/dashboard/selectors';
import { getTrainingModules } from 'features/training/selectors';
import { PermissionsService } from 'shared/services';
import isEqual from 'lodash/isEqual';
import sumBy from 'lodash/sumBy';
import './DashboardContainer.scss';

const hasTcForRenew = (testCenters) => testCenters.some((tc) => tc.isRenewalActive);

const hasTcForEstablish = (testCenters) => testCenters.some((tc) => tc.isEstablishActive);

const iOS =
  !!navigator.platform &&
  (/iPad|iPhone|iPod/.test(navigator.platform) ||
    (/MacIntel/.test(navigator.platform) && navigator.maxTouchPoints > 1));

class DashboardContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMessage: false,
      alertMessage: '',
    };

    this.removeMessage = this.removeMessage.bind(this);
    this.showTermsSuccessMessage = this.showTermsSuccessMessage.bind(this);
  }

  componentDidMount() {
    const { user } = this.props;

    if (user.id) {
      this.props.actions.clearUserAssignmentsAction();
      this.props.actions.getNotViewedNotificationsCount(user.id);
      this.props.actions.getTestCenterPersonnel(user.id).then(() => {
        this.props.actions.getUserAssignments(user.id);
      });
    }

    this.fetchProps();
  }

  componentDidUpdate(prevProps) {
    const { user, terms } = this.props;
    const prevUser = prevProps.user;

    if (!terms.length) {
      if (prevUser.id !== user.id) {
        this.fetchProps(user.id);
      } else if (
        !isEqual(prevUser, user) &&
        (user.testCenterEventPersonnels.length || user.testCenterCyclePersonnels.length)
      ) {
        this.props.actions.getTrainingModules(user.id);
      }
    }
  }

  fetchProps(id) {
    const userId = id || this.props.user.id;
    const { user } = this.props;

    if (!userId) {
      return;
    }

    if (!this.props.testCenters.length) {
      this.props.actions.getUserTestCenters(userId);
    }

    if (
      !this.props.trainingModules.length &&
      (user.testCenterEventPersonnels.length || user.testCenterCyclePersonnels.length)
    ) {
      this.props.actions.getTrainingModules(userId);
    }
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  showTermsSuccessMessage() {
    const { user } = this.props;
    this.setState({
      showMessage: true,
      showMessageTime: 3000,
      alertMessage: 'dashboard.termsAndConditions.successMessage',
      alertMessageParams: { email: user.emailAddress },
    });
  }
  render() {
    const {
      testCenters,
      trainingModules,
      modulesLoading,
      userAssignments,
      user,
      terms,
      termsLoading,
      assignmentsLoading,
      userLoaded,
    } = this.props;

    const showContent = !(termsLoading || !!terms.length);
    const managedTC = testCenters.filter((tc) =>
      PermissionsService.hasTestCenterManage(tc.testCenterCycles[0].id)
    );
    const userIsAssigned = managedTC.length || user.testCenterEventPersonnels.length;
    const totalIncompleteModules = sumBy(trainingModules, 'program.incompletedModules');

    return (
      <div className="DashboardContainer">
        {showContent ? (
          <div>
            <section className="dash-profile">
              <DashboardProfile {...this.props.user} />
            </section>

            <DashboardInvitation />

            {!!managedTC.length && (
              <section className="dash-section col institutions-section">
                <h4 className="fw-bold">
                  {this.props.t('dashboard.associatedTestCenters', {
                    total: managedTC.length,
                  })}
                </h4>
                {hasTcForRenew(managedTC) && (
                  <AlertNotice
                    message={this.props.t('dashboard.testCenterWarning')}
                    type="warning"
                  />
                )}
                {hasTcForEstablish(managedTC) && (
                  <AlertNotice message={this.props.t('dashboard.TCEstablishInfo')} />
                )}
                <div className="card-container">
                  {managedTC?.map((tc) => (
                    <Card
                      key={tc.id}
                      className={`active
                    ${tc.isRenewalActive ? 'renew' : ''}
                    ${tc.isEstablishActive ? 'establish' : ''}`}
                      small>
                      <Link
                        href={`/test-center/${tc.id}`}
                        to={`/test-center/${tc.id}`}
                        className="text-strong text-pacific-blue">
                        <div className="institution-card-content">
                          <div className="fw-bold tc-name mb-0 text-ellipsis-3-lines">
                            {tc.name}
                          </div>
                          <p className="manage-label display-bottom mb-0">
                            <span>{this.props.t('dashboard.manageLabel')}</span>
                            <i className="fa fa-exclamation-triangle ps-2" />
                          </p>
                          <p className="establish-label display-bottom mb-0">
                            <span>{this.props.t('dashboard.labels.establish')}</span>
                            <i className="fa fa-info-circle ps-2" />
                          </p>
                        </div>
                      </Link>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {!assignmentsLoading && userLoaded && (
              <UserAssignments assignments={userAssignments} userIsAssigned={userIsAssigned} />
            )}

            <section className="dash-section col policies">
              <h4 className="fw-bold">{this.props.t('dashboard.policies')}</h4>
              {!!totalIncompleteModules && (
                <AlertNotice
                  message={this.props.t('dashboard.incompleteProgramsModulesWarning', {
                    count: totalIncompleteModules,
                  })}
                  type="warning"
                />
              )}

              {!modulesLoading && !!trainingModules.length ? (
                <div>
                  <div className="card-container mb-3 traning-programs">
                    {trainingModules.length > 1 ? (
                      <Accordion>
                        {trainingModules.map((trainingProgram, index) => (
                          <div
                            data-trigger={
                              trainingProgram.program.incompletedModules ? (
                                <React.Fragment>
                                  {trainingProgram.program.name}
                                  <span>
                                    <i className="fa fa-exclamation-triangle pe-2" />
                                    <span>
                                      {this.props.t('dashboard.incompleteProgramModulesWarning', {
                                        count: trainingProgram.program.incompletedModules,
                                      })}
                                    </span>
                                  </span>
                                </React.Fragment>
                              ) : (
                                trainingProgram.program.name
                              )
                            }
                            key={`pragram-${trainingProgram.program.id}`}>
                            <TrainingModules modules={trainingProgram.trainingModules} small />

                            <Link
                              to={{
                                pathname: '/training',
                                state: { startPosition: index },
                              }}
                              className="btn-link icon-link text-strong">
                              <ArrowRight />
                              {this.props.t('dashboard.viewAllTrainingModules')}
                            </Link>
                          </div>
                        ))}
                      </Accordion>
                    ) : (
                      <div>
                        <TrainingModules modules={trainingModules[0].trainingModules} small />

                        <Link
                          to="/training"
                          href="/training"
                          className="btn-link icon-link text-strong">
                          <ArrowRight />
                          {this.props.t('dashboard.viewAllTrainingModules')}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <AlertNotice message={this.props.t('dashboard.noTrainingModulesMessage')} />
              )}
            </section>
            <AlertMessage
              show={this.state.showMessage}
              time={this.state.showMessageTime}
              removeMessage={this.removeMessage}
              message={this.state.alertMessage}
              messageParams={this.state.alertMessageParams}
            />
          </div>
        ) : (
          <TermsAndConditions user={user} showTermsSuccessMessage={this.showTermsSuccessMessage} />
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    testCenters: state.userDashboard.testCenters,
    user: state.users.currentUser,
    userLoaded: state.users.userLoaded,
    trainingModules: getTrainingModules(state),
    modulesLoading: state.training.loading,
    userAssignments: getMappedAssignments(state),
    termsLoading: state.userDashboard.termsLoading,
    assignmentsLoading: state.userDashboard.assignmentsLoading,
    terms: state.userDashboard.terms,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...trainingActions,
        ...loginActions,
        ...dashboardActions,
        ...tcEventActions,
        ...notificationActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(DashboardContainer));

DashboardContainer.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    emailAddress: PropTypes.string,
    testCenterEventPersonnels: PropTypes.arrayOf(PropTypes.shape({})),
    testCenterCyclePersonnels: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  testCenters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      isRenewalActive: PropTypes.bool,
      isEstablishActive: PropTypes.bool,
      testCenterCycles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  trainingModules: PropTypes.arrayOf(
    PropTypes.shape({
      program: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        incompletedModules: PropTypes.number,
      }),
      trainingModules: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ).isRequired,
  actions: PropTypes.shape({
    getTrainingModules: PropTypes.func.isRequired,
    clearUserAssignmentsAction: PropTypes.func,
    getNotViewedNotificationsCount: PropTypes.func,
    getTestCenterPersonnel: PropTypes.func,
    getUserAssignments: PropTypes.func,
    getUserTestCenters: PropTypes.func,
  }).isRequired,
  modulesLoading: PropTypes.bool.isRequired,
  userAssignments: PropTypes.arrayOf(PropTypes.shape({})),
  terms: PropTypes.arrayOf(PropTypes.shape({})),
  termsLoading: PropTypes.bool,
  assignmentsLoading: PropTypes.bool,
  userLoaded: PropTypes.bool,
  t: PropTypes.func.isRequired,
};
